if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this._tcObserver = null;
      this._tcMutation = null;
      this._tcInterval = null;
      this._lastLoadedId = null;
    }

    hide() {
      this._stopWatchers();
      super.hide();
    }

    show(opener) {
      super.show(opener);
      this.showActiveMedia();
      this._startWatchers(); // ✅ IMPORTANT: handle mobile swipe sliders (transform-based)
    }

    showActiveMedia() {
      const openedId = this.openedBy?.getAttribute("data-media-id");
      if (!openedId) return;

      // Remove active class from other media
      this.querySelectorAll(`[data-media-id]:not([data-media-id="${openedId}"])`).forEach((el) => {
        el.classList.remove('active');
      });

      const activeMedia = this.querySelector(`[data-media-id="${openedId}"]`);
      if (!activeMedia) return;

      activeMedia.classList.add('active');
      activeMedia.scrollIntoView({ block: 'nearest', inline: 'center' });

      // Center if container exists
      const container = this.querySelector('[role="document"]');
      if (container) {
        const left =
          activeMedia.offsetLeft -
          (container.clientWidth - activeMedia.clientWidth) / 2;
        container.scrollLeft = Math.max(0, left);
      }

      // Load deferred for the opener media immediately
      this._loadDeferredFromElement(activeMedia);
    }

    /* -----------------------------
      CORE: Find current "active slide"
      Works across Swiper/Flickity/Splide/Slick/custom
    ------------------------------*/
    _getCurrentActiveSlide() {
      // Try common "active" selectors used by sliders
      return (
        this.querySelector('[data-media-id].active') ||
        this.querySelector('[data-media-id].is-selected') ||
        this.querySelector('[data-media-id].swiper-slide-active') ||
        this.querySelector('[data-media-id].splide__slide.is-active') ||
        this.querySelector('[data-media-id].slick-current') ||
        this.querySelector('[data-media-id][aria-hidden="false"]') ||
        this.querySelector('[data-media-id]')
      );
    }

    _loadDeferredFromElement(el) {
      if (!el) return;

      const id = el.getAttribute?.('data-media-id') || null;
      if (id && this._lastLoadedId === id) return;

      // Case 1: slide itself is deferred-media
      if (el.nodeName === 'DEFERRED-MEDIA' && typeof el.loadContent === 'function') {
        el.loadContent();
        this._lastLoadedId = id || this._lastLoadedId;
        return;
      }

      // Case 2: deferred-media inside the slide
      const deferred = el.querySelector?.('deferred-media');
      if (deferred && typeof deferred.loadContent === 'function') {
        deferred.loadContent();
        this._lastLoadedId = id || this._lastLoadedId;
        return;
      }

      // Case 3: sometimes the slide IS the wrapper and deferred-media is sibling/child deeper
      const deepDeferred = this.querySelector?.(`[data-media-id="${id}"] deferred-media`);
      if (deepDeferred && typeof deepDeferred.loadContent === 'function') {
        deepDeferred.loadContent();
        this._lastLoadedId = id || this._lastLoadedId;
      }
    }

    _tickLoadActive() {
      const activeSlide = this._getCurrentActiveSlide();
      this._loadDeferredFromElement(activeSlide);
    }

    _startWatchers() {
      // 1) MutationObserver: reacts when slider changes classes/aria-hidden
      if (!this._tcMutation) {
        this._tcMutation = new MutationObserver(() => this._tickLoadActive());
        this._tcMutation.observe(this, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'aria-hidden', 'style']
        });
      }

      // 2) Interval fallback (covers sliders that don't mutate observed attrs reliably)
      if (!this._tcInterval) {
        this._tcInterval = setInterval(() => this._tickLoadActive(), 200);
      }

      // 3) Immediate tick
      setTimeout(() => this._tickLoadActive(), 50);
    }

    _stopWatchers() {
      if (this._tcMutation) {
        this._tcMutation.disconnect();
        this._tcMutation = null;
      }
      if (this._tcInterval) {
        clearInterval(this._tcInterval);
        this._tcInterval = null;
      }
      this._lastLoadedId = null;
    }
  });
}
